/**
 * useDataGrid Hook (RPC-based)
 *
 * Custom hook for managing data grid state and API calls using TanStack Query and Hono RPC.
 * Handles pagination, sorting, filtering, and data fetching with automatic
 * cache management and type-safe API calls.
 */

import { useQuery, keepPreviousData, type UseQueryResult } from '@tanstack/react-query';
import { useState, useCallback, useMemo, useRef } from 'react';
import * as React from 'react';
import {
  PAGINATION_TOTAL_USE_CACHE,
  type DataGridRequest,
  type DataGridResponse,
  type DataGridFilter,
  type DataGridSortDirection,
  type DataGridLoadSuccessCallback,
  type DataGridLoadErrorCallback,
  type DataGridStateChangeCallback,
} from '@loader/data-grid';

import { useRpc } from '@/lib/rpc';

/**
 * Supported data-grid endpoint types for RPC calls
 *
 * Note: For table/version data queries (parquet data), use customQueryFn
 * with the unified query endpoint that returns signed tokens for DuckDB API.
 */
export type DataGridEndpoint =
  | 'tables'
  | 'linked-tables'
  | 'versions'
  | 'workspace-members'
  | 'api-keys'
  | 'audit-logs';

/**
 * Configuration options for useDataGrid hook
 *
 * @template TData - Type of data items returned by the grid
 * @template TExtraParams - Type for endpoint-specific query parameters
 */
export interface UseDataGridOptions<TData, TExtraParams extends object = Record<string, unknown>> {
  /**
   * Endpoint type for RPC calls (preferred over customQueryFn for standard endpoints)
   * Use this for GET endpoints that follow the data-grid contract
   */
  endpoint?: DataGridEndpoint;
  /**
   * Custom query function for specialized endpoints (e.g., POST requests)
   * Receives DataGridRequest params and should return DataGridResponse
   * If provided, endpoint parameter is ignored
   */
  customQueryFn?: (params: DataGridRequest) => Promise<DataGridResponse<TData>>;
  /**
   * Additional query params to append (e.g., table_id, version_id, workspace_id)
   */
  extraParams?: TExtraParams;
  /** Initial page size (default: 10) */
  initialPageSize?: number;
  /** Initial sort column */
  initialSortBy?: string;
  /** Initial sort direction */
  initialSortDir?: DataGridSortDirection;
  /** Initial filters */
  initialFilters?: DataGridFilter[];
  /** Initial search term */
  initialSearch?: string;
  /** Enable automatic refetching on window focus (default: true) */
  refetchOnWindowFocus?: boolean;
  /** Stale time in milliseconds (default: 30000 = 30 seconds) */
  staleTime?: number;
  /** Cache time in milliseconds (default: 300000 = 5 minutes) */
  cacheTime?: number;
  /** Callback fired when data is successfully loaded */
  onLoadSuccess?: DataGridLoadSuccessCallback<TData>;
  /** Callback fired when data loading fails */
  onLoadError?: DataGridLoadErrorCallback;
  /** Callback fired when grid state changes */
  onStateChange?: DataGridStateChangeCallback;
  /** Custom query key prefix for cache management */
  queryKeyPrefix?: string;
  /** Enable/disable the query (default: true) */
  enabled?: boolean;
  /** Throw errors to error boundary (default: true from QueryClient). Set to false for inline error handling. */
  throwOnError?: boolean;
}

/**
 * Internal state for the data grid
 */
interface DataGridState {
  pageIndex: number;
  pageSize: number;
  sort_by?: string;
  sort_dir?: DataGridSortDirection;
  filters: DataGridFilter[];
  search?: string;
}

/**
 * Return type for useDataGrid hook
 */
export interface UseDataGridReturn<TData> {
  data: TData[];
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;
  queryResult: UseQueryResult<DataGridResponse<TData>, Error>;
  pagination: {
    total: number;
    pageIndex: number;
    pageSize: number;
    pageCount: number;
    offset: number;
    remaining: number;
  };
  sorting: {
    sort_by?: string;
    sort_dir?: DataGridSortDirection;
  };
  filters: DataGridFilter[];
  search?: string;
  schema?: DataGridResponse<TData>['schema'];
  actions: {
    nextPage: () => void;
    previousPage: () => void;
    setPage: (pageIndex: number) => void;
    setPageSize: (pageSize: number) => void;
    setSort: (sort_by: string | undefined, sort_dir?: DataGridSortDirection) => void;
    setFilter: (filter: DataGridFilter) => void;
    removeFilter: (column_id: string) => void;
    clearFilters: () => void;
    setSearch: (search: string | undefined) => void;
    reset: () => void;
    refetch: () => void;
  };
}

/**
 * Build query parameters for RPC calls
 */
function buildRpcQuery(
  params: DataGridRequest,
  extraParams?: Record<string, unknown>
): Record<string, string | undefined> {
  const query: Record<string, string | undefined> = {};

  if (params.limit !== undefined) {
    query.limit = params.limit.toString();
  }
  if (params.offset !== undefined) {
    query.offset = params.offset.toString();
  }
  if (params.sort_by) {
    query.sort_by = params.sort_by;
  }
  if (params.sort_dir) {
    query.sort_dir = params.sort_dir;
  }
  if (params.filters && params.filters.length > 0) {
    query.filters = JSON.stringify(params.filters);
  }
  if (params.search) {
    query.search = params.search;
  }

  // Add extra params
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      if (value !== undefined && value !== null) {
        query[key] = String(value);
      }
    }
  }

  return query;
}

/**
 * Custom hook for managing data grid with server-side data fetching via RPC
 *
 * @example Standard GET endpoint with RPC
 * ```tsx
 * const grid = useDataGrid<Table>({
 *   endpoint: 'tables',
 *   initialPageSize: 20,
 * });
 * ```
 *
 * @example With extra parameters
 * ```tsx
 * const grid = useDataGrid<Record<string, unknown>>({
 *   endpoint: 'records',
 *   extraParams: { table_id: tableId, version_id: versionId },
 *   initialPageSize: 50,
 * });
 * ```
 *
 * @example Custom query function (e.g., POST endpoint)
 * ```tsx
 * const rpc = useRpc();
 * const grid = useDataGrid<LinkedTableData>({
 *   customQueryFn: async (params) => {
 *     const res = await rpc.api.private['linked-tables'][':id'].preview.$post({
 *       param: { id: linkedTableId },
 *       json: config,
 *       query: params,
 *     });
 *     if (!res.ok) throw new Error('Failed to preview');
 *     return await res.json();
 *   },
 * });
 * ```
 */
export function useDataGrid<TData = unknown, TExtraParams extends object = Record<string, unknown>>(
  options: UseDataGridOptions<TData, TExtraParams>
): UseDataGridReturn<TData> {
  const {
    endpoint,
    customQueryFn,
    extraParams,
    initialPageSize = 10,
    initialSortBy,
    initialSortDir,
    initialFilters = [],
    initialSearch,
    refetchOnWindowFocus = true,
    staleTime = 30000,
    cacheTime = 300000,
    onLoadSuccess,
    onLoadError,
    onStateChange,
    queryKeyPrefix = 'dataGrid',
    enabled = true,
    throwOnError,
  } = options;

  const rpc = useRpc();

  // Validate options
  if (enabled && !endpoint && !customQueryFn) {
    throw new Error('useDataGrid: Either endpoint or customQueryFn must be provided');
  }

  // Cache the total count from first page to avoid re-running count query
  // Backend returns -1 for total on non-first pages to signal "use cached value"
  const cachedTotalRef = useRef<number | null>(null);

  // Internal state
  const [state, setState] = useState<DataGridState>({
    pageIndex: 0,
    pageSize: initialPageSize,
    sort_by: initialSortBy,
    sort_dir: initialSortDir,
    filters: initialFilters,
    search: initialSearch,
  });

  // Build request parameters
  const requestParams: DataGridRequest = useMemo(() => ({
    limit: state.pageSize,
    offset: state.pageIndex * state.pageSize,
    sort_by: state.sort_by,
    sort_dir: state.sort_dir,
    filters: state.filters.length > 0 ? state.filters : undefined,
    search: state.search,
  }), [state]);

  // Query key for cache management
  const queryKey = useMemo(() => {
    const endpointKey = customQueryFn ? 'custom' : endpoint;
    return [queryKeyPrefix, endpointKey, requestParams, extraParams];
  }, [queryKeyPrefix, endpoint, customQueryFn, requestParams, extraParams]);

  // RPC-based fetch function
  const fetchData = useCallback(async (): Promise<DataGridResponse<TData>> => {
    // Use custom query function if provided
    if (customQueryFn) {
      return await customQueryFn(requestParams);
    }

    // Build query params for RPC
    const query = buildRpcQuery(requestParams, extraParams as Record<string, unknown>);

    // Route to appropriate RPC endpoint
    let res: Response;

    switch (endpoint) {
      case 'tables':
        res = await rpc.api.private.tables.$get({ query });
        break;

      case 'linked-tables':
        res = await rpc.api.private['linked-tables'].$get({ query });
        break;

      case 'versions':
        res = await rpc.api.private.versions.$get({ query });
        break;

      case 'workspace-members': {
        const workspaceId = (extraParams as { workspace_id?: string })?.workspace_id;
        if (!workspaceId) {
          throw new Error('workspace_id is required for workspace-members endpoint');
        }
        // @ts-expect-error - Hono RPC type inference limitation for nested routes
        res = await rpc.api.private.workspaces[':id'].members.$get({
          param: { id: workspaceId },
          query,
        });
        break;
      }

      case 'api-keys':
        res = await rpc.api.private.tokens.$get({ query });
        break;

      case 'audit-logs':
        res = await rpc.api.private['audit-logs'].$get({ query });
        break;

      default:
        throw new Error(`Unknown endpoint: ${endpoint}`);
    }

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}));
      throw new Error((errorData as { error?: string }).error || `Failed to fetch ${endpoint}`);
    }

    return await res.json() as DataGridResponse<TData>;
  }, [endpoint, customQueryFn, rpc, requestParams, extraParams]);

  // TanStack Query with keepPreviousData for smooth page transitions
  const queryResult = useQuery<DataGridResponse<TData>, Error>({
    queryKey,
    queryFn: fetchData,
    enabled: enabled && (!!endpoint || !!customQueryFn),
    refetchOnWindowFocus,
    staleTime,
    gcTime: cacheTime,
    // Keep previous data during page transitions to prevent UI flicker
    placeholderData: keepPreviousData,
    // Allow overriding throwOnError for inline error handling (undefined uses QueryClient default)
    ...(throwOnError !== undefined && { throwOnError }),
  });

  // Update cached total when we get a valid total from the server
  // Backend sends PAGINATION_TOTAL_USE_CACHE (-1) on non-first pages to signal "use cached"
  React.useEffect(() => {
    const serverTotal = queryResult.data?.pagination?.total;
    // Only update cache if server returned a valid total (not the "use cache" sentinel)
    if (typeof serverTotal === 'number' && serverTotal !== PAGINATION_TOTAL_USE_CACHE) {
      cachedTotalRef.current = serverTotal;
    }
  }, [queryResult.data?.pagination?.total]);

  // Reset cached total when filters/search/sort change (data set changes)
  // Note: extraParams changes are handled separately below (pageIndex reset to 0
  // ensures the first-page fetch will naturally refresh the cached total)
  React.useEffect(() => {
    cachedTotalRef.current = null;
  }, [state.filters, state.search, state.sort_by, state.sort_dir]);

  // Stable key for extraParams comparison (used by pageIndex reset effect below)
  const extraParamsKey = JSON.stringify(extraParams);

  // Reset page index when extraParams change (the underlying data set changes completely)
  // Without this, the grid stays on e.g. page 3 after a config change, the backend
  // skips the COUNT query (offset !== 0), and pagination shows 0 total / 1 page.
  // Use a ref to skip the initial render (pageIndex is already 0).
  const prevExtraParamsKeyRef = useRef(extraParamsKey);
  React.useEffect(() => {
    if (prevExtraParamsKeyRef.current !== extraParamsKey) {
      prevExtraParamsKeyRef.current = extraParamsKey;
      setState((prev) => (prev.pageIndex !== 0 ? { ...prev, pageIndex: 0 } : prev));
    }
  }, [extraParamsKey]);

  // Success callback
  React.useEffect(() => {
    if (queryResult.isSuccess && queryResult.data) {
      onLoadSuccess?.(queryResult.data);
    }
  }, [queryResult.isSuccess, queryResult.data, onLoadSuccess]);

  // Error callback
  React.useEffect(() => {
    if (queryResult.isError && queryResult.error) {
      onLoadError?.(queryResult.error);
    }
  }, [queryResult.isError, queryResult.error, onLoadError]);

  // State change callback
  React.useEffect(() => {
    onStateChange?.(requestParams);
  }, [requestParams, onStateChange]);

  // Pagination metadata - use cached total when server sends PAGINATION_TOTAL_USE_CACHE
  const pagination = useMemo(() => {
    const serverTotal = queryResult.data?.pagination?.total;
    // Use server total if valid, otherwise use cached total from first page
    // Server sends PAGINATION_TOTAL_USE_CACHE (-1) to signal "use cached value"
    const total = (typeof serverTotal === 'number' && serverTotal !== PAGINATION_TOTAL_USE_CACHE)
      ? serverTotal
      : (cachedTotalRef.current ?? 0);
    const pageCount = Math.ceil(total / state.pageSize) || 1;

    return {
      total,
      pageIndex: state.pageIndex,
      pageSize: state.pageSize,
      pageCount,
      offset: state.pageIndex * state.pageSize,
      remaining: queryResult.data?.pagination?.remaining ?? Math.max(0, total - (state.pageIndex + 1) * state.pageSize),
    };
  }, [queryResult.data, state.pageIndex, state.pageSize]);

  // Actions
  const actions = useMemo(() => ({
    nextPage: () => {
      // Note: No upper-bound clamping here - DataTable disables the Next button at last page
      // Clamping with captured pagination.pageCount caused stale closure bugs
      setState((prev) => ({
        ...prev,
        pageIndex: prev.pageIndex + 1,
      }));
    },

    previousPage: () => {
      setState((prev) => ({
        ...prev,
        pageIndex: Math.max(prev.pageIndex - 1, 0),
      }));
    },

    setPage: (pageIndex: number) => {
      // Note: Only lower-bound clamping - DataTable handles upper bound before calling
      // Upper-bound clamping with captured pagination.pageCount caused stale closure bugs
      setState((prev) => ({
        ...prev,
        pageIndex: Math.max(0, pageIndex),
      }));
    },

    setPageSize: (pageSize: number) => {
      setState((prev) => ({
        ...prev,
        pageSize,
        pageIndex: 0,
      }));
    },

    setSort: (sort_by: string | undefined, sort_dir?: DataGridSortDirection) => {
      setState((prev) => ({
        ...prev,
        sort_by,
        sort_dir: sort_dir ?? (sort_by ? 'asc' : undefined),
        pageIndex: 0,
      }));
    },

    setFilter: (filter: DataGridFilter) => {
      setState((prev) => {
        const existingIndex = prev.filters.findIndex(
          (f) => f.column_id === filter.column_id
        );

        const newFilters =
          existingIndex >= 0
            ? [
                ...prev.filters.slice(0, existingIndex),
                filter,
                ...prev.filters.slice(existingIndex + 1),
              ]
            : [...prev.filters, filter];

        return {
          ...prev,
          filters: newFilters,
          pageIndex: 0,
        };
      });
    },

    removeFilter: (column_id: string) => {
      setState((prev) => ({
        ...prev,
        filters: prev.filters.filter((f) => f.column_id !== column_id),
        pageIndex: 0,
      }));
    },

    clearFilters: () => {
      setState((prev) => ({
        ...prev,
        filters: [],
        pageIndex: 0,
      }));
    },

    setSearch: (search: string | undefined) => {
      setState((prev) => ({
        ...prev,
        search,
        pageIndex: 0,
      }));
    },

    reset: () => {
      setState({
        pageIndex: 0,
        pageSize: initialPageSize,
        sort_by: initialSortBy,
        sort_dir: initialSortDir,
        filters: initialFilters,
        search: initialSearch,
      });
    },

    refetch: () => {
      queryResult.refetch();
    },
  }), [
    initialPageSize,
    initialSortBy,
    initialSortDir,
    initialFilters,
    initialSearch,
    queryResult,
  ]);

  return {
    data: queryResult.data?.data ?? [],
    isLoading: queryResult.isLoading,
    error: queryResult.error ?? null,
    isFetching: queryResult.isFetching,
    queryResult,
    pagination,
    sorting: {
      sort_by: state.sort_by,
      sort_dir: state.sort_dir,
    },
    filters: state.filters,
    search: state.search,
    schema: queryResult.data?.schema,
    actions,
  };
}
